import { z } from "zod";
import { ActorId, IsoUtc, Uuid } from "./primitives";

/**
 * People safety (docs/08 section 6, docs/06 section 2; ADR-0013): report a person from their card, moderators review
 * reports with a protected snapshot of the person's recent lines and act (mute for 24 h, ban, resolve, dismiss), and
 * accounts mute people for themselves (`/me/blocks`, the spec's recipient block). Reporters are never shown to the
 * reported person.
 */

/** Prohibited-content categories offered when reporting a person (docs/08 section 6). */
export const PersonReportCategory = z.enum(["harassment", "threats", "sexual", "hate", "spam_scam", "impersonation", "other"]);
export type PersonReportCategory = z.infer<typeof PersonReportCategory>;
export const PERSON_REPORT_CATEGORY_LABEL: Record<PersonReportCategory, string> = {
  harassment: "Harassment or bullying",
  threats: "Threats or violence",
  sexual: "Sexual content",
  hate: "Hate or slurs",
  spam_scam: "Spam, scams or stealing accounts",
  impersonation: "Pretending to be someone else",
  other: "Something else",
};

/**
 * POST /reports: report a person in the reporter's current room (guests and accounts), or in a private conversation
 * (ADR-0014; the snapshot is then the conversation's recent messages, both sides). Exactly one of roomKey/conversationId.
 */
export const PersonReportRequest = z
  .object({
    targetActorId: ActorId,
    /** The room the reporter is in (street or interior key); the snapshot is the person's recent lines there. */
    roomKey: z.string().min(1).max(80).optional(),
    conversationId: Uuid.optional(),
    /**
     * Optional: the one message being reported (a room chat line or a private message). The snapshot includes it, marked
     * as the reported line, when it is that person's, in that room / conversation, and still stored; otherwise the report
     * is kept without it (`messageIncluded: false`).
     */
    messageId: Uuid.optional(),
    category: PersonReportCategory,
    note: z.string().max(500).optional(),
  })
  .strict()
  .refine((r) => (r.roomKey ? 1 : 0) + (r.conversationId ? 1 : 0) === 1, { message: "give either roomKey or conversationId", path: ["roomKey"] });
export type PersonReportRequest = z.infer<typeof PersonReportRequest>;
export const PersonReportResponse = z.object({
  reportId: Uuid,
  /** True when the same reporter already had an open report about this person from the last 10 minutes. */
  duplicate: z.boolean(),
  /** Only when a `messageId` was sent: whether that message is in the report's snapshot (also added to a duplicate). */
  messageIncluded: z.boolean().optional(),
});
export type PersonReportResponse = z.infer<typeof PersonReportResponse>;

/** One line of the protected snapshot; in a conversation report, who wrote it. */
export const ReportedLine = z.object({
  messageId: Uuid,
  text: z.string(),
  at: IsoUtc,
  by: z.enum(["reported", "reporter"]).optional(),
  /** The line the reporter picked ("Report this message"); moderators see it highlighted. */
  reported: z.boolean().optional(),
});
export type ReportedLine = z.infer<typeof ReportedLine>;

export const PersonReportStatus = z.enum(["open", "resolved", "dismissed"]);
export type PersonReportStatus = z.infer<typeof PersonReportStatus>;

/** Moderator view of a report (moderator/admin only). */
export const ModeratorReportView = z.object({
  reportId: Uuid,
  status: PersonReportStatus,
  category: PersonReportCategory,
  note: z.string(),
  createdAt: IsoUtc,
  /** Where it happened: room chat (roomKey) or a private conversation (ADR-0014). */
  source: z.enum(["room", "conversation"]).default("room"),
  roomKey: z.string(),
  target: z.object({ actorId: z.string(), label: z.string(), kind: z.enum(["guest", "resident", "ai_resident"]) }),
  reporterLabel: z.string(),
  /** The person's recent lines in that room when the report was made (newest last). */
  lines: z.array(ReportedLine),
  /** Earlier reports about the same person (any status), for context. */
  priorReports: z.number().int().min(0),
  resolvedAt: IsoUtc.nullable(),
  resolution: z.string().nullable(),
});
export type ModeratorReportView = z.infer<typeof ModeratorReportView>;

export const ReportQueueQuery = z.object({
  status: PersonReportStatus.default("open"),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export const ReportQueueResponse = z.object({ items: z.array(ModeratorReportView), nextCursor: z.string().nullable() });
export type ReportQueueResponse = z.infer<typeof ReportQueueResponse>;

/**
 * A moderator's decision. `mute_24h` and `ban` also restrict the person (moderation_actions; the realtime rooms mute or
 * disconnect them within about a minute); AI residents cannot be muted or banned. A note is always required (audit).
 */
export const ReportAction = z.enum(["dismiss", "resolve", "mute_24h", "ban"]);
export type ReportAction = z.infer<typeof ReportAction>;
export const ReportDecisionRequest = z.object({ action: ReportAction, note: z.string().min(1).max(500) }).strict();
export type ReportDecisionRequest = z.infer<typeof ReportDecisionRequest>;

// ---------------- Mutes (recipient blocks; accounts) ----------------
export const BlockView = z.object({ blockId: Uuid, actorId: z.string(), label: z.string().nullable(), createdAt: IsoUtc });
export type BlockView = z.infer<typeof BlockView>;
export const MyBlocksResponse = z.object({ items: z.array(BlockView) });
export type MyBlocksResponse = z.infer<typeof MyBlocksResponse>;
export const BlockRequest = z.object({ actorId: ActorId }).strict();
export type BlockRequest = z.infer<typeof BlockRequest>;
/** Most people one account can mute. */
export const MAX_BLOCKS_PER_ACCOUNT = 500;
